import { ref, computed } from "vue";

import { store } from "@/store";
import { canvas } from "@/canvas-api/Canvas";

const isRotated = computed<boolean>(() =>
  store.getters.canvasOrientation === "rotated" ? true : false
);

const viewPortPadding = 200; //100 top and bottom/left and right
export const viewPortWidth = computed<number>(() => store.getters.bleedWidth + viewPortPadding);
export const viewPortHeight = computed<number>(() => store.getters.bleedHeight + viewPortPadding);

export const scrollBarHorizontal = ref<HTMLDivElement>();
export const scrollBarVertical = ref<HTMLDivElement>();
export const scrollerHorizontal = ref<HTMLDivElement>();
export const scrollerVertical = ref<HTMLDivElement>();

export const viewPortValueX = ref(0);
export const viewPortValueY = ref(0);

export const setViewPortValue = (val: number, axis: string) => {
  if (canvas.value.viewportTransform) {
    switch (axis) {
      case "x": {
        const scale = getHorizScrollerScale();

        if (scale) {
          val = val / scale;
        }

        const vpt: number[] = canvas.value.viewportTransform;

        vpt[4] = vpt[4] - val;

        canvas.value.setViewportTransform(vpt);

        viewPortValueX.value = val;
        break;
      }
      case "y": {
        const scale = getVerticalScrollerScale();

        if (scale) {
          val = val / scale;
        }

        const vpt: number[] = canvas.value.viewportTransform;

        vpt[5] = vpt[5] - val;

        canvas.value.setViewportTransform(vpt);

        viewPortValueY.value = val;
        break;
      }
    }
  }
};

export const centerCanvasViewPort = () => {
  if (canvas.value.viewportTransform) {
    const vpt: number[] = canvas.value.viewportTransform;

    vpt[0] = 1; //zoomX
    vpt[3] = 1; //zoomY
    vpt[4] = 0; //translateX
    vpt[5] = 0; //translateY

    viewPortValueX.value = 0;
    viewPortValueY.value = 0;

    canvas.value.setViewportTransform(vpt);
  }
};

const setScrollerStyle = (element: HTMLDivElement, property: string, value: string) => {
  element.style.setProperty(property, value);
};

export const getHorizScrollerScale = () => {
  if (scrollBarHorizontal.value) {
    if (isRotated.value) {
      return (
        scrollBarHorizontal.value.clientWidth / (viewPortHeight.value * canvas.value.getZoom())
      );
    } else {
      return scrollBarHorizontal.value.clientWidth / (viewPortWidth.value * canvas.value.getZoom());
    }
  }
};

export const getVerticalScrollerScale = () => {
  if (scrollBarVertical.value) {
    if (isRotated.value) {
      return scrollBarVertical.value.clientHeight / (viewPortWidth.value * canvas.value.getZoom());
    } else {
      return scrollBarVertical.value.clientHeight / (viewPortHeight.value * canvas.value.getZoom());
    }
  }
};

// prettier-ignore
export const setHorizScrollerWidth = () => {
  if (scrollBarHorizontal.value && scrollerHorizontal.value) {
    const scale = getHorizScrollerScale();

    if (scale && scale < 1) {
      setScrollerStyle(scrollerHorizontal.value, "width", `${scrollBarHorizontal.value.offsetWidth * scale}px`);

      if (scrollerHorizontal.value.style.visibility !== "visible") {
        setScrollerStyle(scrollerHorizontal.value, "visibility", "visible");
      }
    } else if (scale && scale >= 1 && scrollerHorizontal.value.style.visibility !== "hidden") {
      setScrollerStyle(scrollerHorizontal.value, "width", `${scrollBarHorizontal.value.offsetWidth}px`);
      setScrollerStyle(scrollerHorizontal.value, "visibility", "hidden");
    }
  }
};

// prettier-ignore
export const setVertScrollerHeight = () => {
  if (scrollBarVertical.value && scrollerVertical.value) {
    const scale = getVerticalScrollerScale();

    if (scale && scale < 1) {
      setScrollerStyle(scrollerVertical.value, "height", `${scrollBarVertical.value.offsetHeight * scale}px`);

      if (scrollerVertical.value.style.visibility !== "visible") {
        setScrollerStyle(scrollerVertical.value, "visibility", "visible");
      }
    } else if (scale && scale >= 1 && scrollerVertical.value.style.visibility !== "hidden") {
      setScrollerStyle(scrollerVertical.value, "height",`${scrollBarVertical.value.offsetHeight}px`);
      setScrollerStyle(scrollerVertical.value, "visibility", "hidden");
    }
  }
};

// prettier-ignore
export const centerHorizScroller = () => {
  if (scrollBarHorizontal.value && scrollerHorizontal.value) {
    const position = scrollBarHorizontal.value.offsetWidth / 2 - scrollerHorizontal.value.offsetWidth / 2;
    setScrollerStyle(scrollerHorizontal.value, "left", `${position}px`);
  }
};

// prettier-ignore
export const centerVertScroller = () => {
  if (scrollBarVertical.value && scrollerVertical.value) {
    const position = scrollBarVertical.value.offsetHeight / 2 - scrollerVertical.value.offsetHeight / 2;
    setScrollerStyle(scrollerVertical.value, "top", `${position}px`);
  }
};

// prettier-ignore
export const setHorizScrollerMaxWidth = () => {
  if (scrollBarHorizontal.value && scrollerHorizontal.value) {
    setScrollerStyle(scrollerHorizontal.value, "width", `${scrollBarHorizontal.value.offsetWidth}px`);
    setScrollerStyle(scrollerHorizontal.value, "left", "0px");
    setScrollerStyle(scrollerHorizontal.value, "visibility", "hidden");
  }
};

// prettier-ignore
export const setVertScrollerMaxHeight = () => {
  if (scrollBarVertical.value && scrollerVertical.value) {
    setScrollerStyle(scrollerVertical.value, "height", `${scrollBarVertical.value.offsetHeight}px`);
    setScrollerStyle(scrollerVertical.value, "top", "0px");
    setScrollerStyle(scrollerVertical.value, "visibility", "hidden");
  }
};

export const centerAndSizeScrollers = () => {
  setHorizScrollerWidth();
  setVertScrollerHeight();
  centerHorizScroller();
  centerVertScroller();
};
